import React, { Dispatch, useEffect, useRef, useState } from "react";
import { Box, Button, Modal, TextField } from "@mui/material";
import TileLayer from "ol/layer/Tile";
import OSM from "ol/source/OSM";
import { fromLonLat, toLonLat } from "ol/proj";

import { Map, MapBrowserEvent, Overlay, View } from "ol";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLocationDot } from "@fortawesome/sharp-duotone-light-svg-icons";
import { mapBoxAccessToken } from "../../app/env";
import { useDispatch } from "react-redux";
import { AnyAction } from "@reduxjs/toolkit";
import { clickOnMap } from "../../features/riskViewer/riskViewerSlice";
import { DELETE_CONFIRMATION_NO, LOCATION_SELECT_MODAL_INSTRUCTIONS, VALID_LOCATION } from "../UserVisibleStrings";
import Typography from "@mui/material/Typography";

type OpenLayersModalProps = {
    open: boolean,
    handleClose: () => void,
    index: number,
};
const UNKNOWN_LOCATION = "Unknown location";

/**
 * When the user selects a point from the map, we need to store that data in redux so it can update the input form accordingly
 * @param lngLat the coordinate (in degrees lat and long) where the user clicked
 * @param dispatch how to dispatch to redux
 * @param index the position of the waypoint to update (if undefined, it will be appended to the end)
 */
export function selectWaypointFromMap(
    lngLat : { lat: number, lng: number },
    dispatch: Dispatch<AnyAction>,
    index: number | undefined
)
{
    reverseGeocode( lngLat.lat, lngLat.lng )
        .then( placeName =>
        {
            dispatch( clickOnMap( { location: lngLat, placeName, index } ) );
        } )
        .catch( err => console.error( err ) );
}
export async function getPlaceName( lat: number | undefined, lng: number | undefined ): Promise<string | undefined>
{
    if ( lat === undefined || lng === undefined )
    {
        return undefined;
    }

    try
    {
        return await reverseGeocode( lat, lng );
    }
    catch ( err )
    {
        console.error( err );
        return undefined;
    }
}

export function getMapboxGeocodeUrl( lat: number, lon: number, includeBoundingBox: boolean )
{
    return getMapboxGeocodeUrlQuery( `${lon},${lat}`, includeBoundingBox );
}
export function getMapboxGeocodeUrlQuery( query: string, includeBoundingBox: boolean )
{
    return `https://api.mapbox.com/geocoding/v5/mapbox.places/${query}.json?access_token=${mapBoxAccessToken}` +
        ( includeBoundingBox ? `&bbox=${MIN_LON},${MIN_LAT},${MAX_LON},${MAX_LAT}` : "" );

}
const reverseGeocode = async ( lat: number, lon: number ) =>
{
    const response = await fetch( getMapboxGeocodeUrl( lat, lon, true ) );
    if ( !response.ok )
    {
        throw new Error( "Reverse geocoding failed" );
    }
    const data = await response.json();
    const place = data.features[0]?.place_name;
    // We can't use ?? below because we cannot allow the empty string
    return place.length > 0 ? place : UNKNOWN_LOCATION;
};

/**
 * Place names must be defined (have a valid geocoded address) and be within the United States, Canada, or Mexico,
 * but must exclude the state of Hawaii.
 * @param placeName the reverse geocoded location name/address
 */
export function isValidPlacename( placeName: string ): boolean
{
    if ( placeName === UNKNOWN_LOCATION )
    {
        return false;
    }

    const normalized = placeName.toLowerCase().trim();

    const isInSupportedCountry =
        normalized.endsWith( "united states" ) ||
        normalized.endsWith( "canada" ) ||
        normalized.endsWith( "mexico" );

    // Match common Hawaii address patterns like:
    // "Makawao, Hawaii 96768, United States"
    // Hawaii, United States
    const hawaiiRegex = /\b(hawaii)(\s+\d{5})?,\s*united states$/;

    const isHawaii = hawaiiRegex.test( normalized );

    return isInSupportedCountry && !isHawaii;
}

// Define the bounds for the mapbox autocomplete query
const MAX_LON = -50;
const MIN_LON = -170;
const MIN_LAT = 14;
const MAX_LAT = 83;
/**
 * Locations must be in the continental united states / mexico / canada
 * We check elsewhere if a location is in the water (via the reverse geocoding placename resolution), so this method only needs to
 * be a bounding box to exclude alaska and hawaii.
 */
export function isValidLocation( lat:number, lng: number )
{
    if ( lng < MIN_LON || lng > MAX_LON )
    {
        return false;
    }
    if ( lat < MIN_LAT || lat > MAX_LAT )
    {
        return false;
    }
    return true;
}
const OpenLayersModal = ( { open, handleClose, index }: OpenLayersModalProps ) =>
{
    const [ lat, setLat ] = useState<number|undefined>( undefined );
    const [ lon, setLon ] = useState<number|undefined>( undefined );
    const [ firstClick, setFirstClick ] = useState<boolean>( true );
    const [ place, setPlace ] = useState( "" );

    const mapRef = useRef<HTMLDivElement>( null );
    const popupRef = useRef( null );
    const dispatch = useDispatch();

    const handleSelect = () =>
    {
        if ( lat !== undefined && lon !== undefined )
        {
            selectWaypointFromMap( { lat: lat, lng: lon }, dispatch, index );
            handleClose();
        }
    };
    const curr = mapRef?.current;
    const popup = popupRef?.current;
    useEffect( () =>
    {
        if ( curr === null || popup === null ) return;
        const osmLayer = new TileLayer( {
            preload: Infinity,
            source: new OSM(),
        } );
        const popupOverlay = new Overlay( {
            positioning: "bottom-center",
            element: popup,
            offset: [ 0, -10 ],
            autoPan: {
                animation: {
                    duration: 250,
                },
            },
        } );
        const map = new Map( {
            target: curr,
            layers: [ osmLayer ],
            view: new View( {
                center: fromLonLat( [ -97, 40 ] ),
                zoom: 4,
            } ),
            overlays: [ popupOverlay ]
        } );

        const handleMapClick = async ( event: MapBrowserEvent<any> ) => // eslint-disable-line  @typescript-eslint/no-explicit-any
        {
            const coordinates = toLonLat( event.coordinate );
            const latitude = coordinates[1];
            const longitude = coordinates[0];
            const placeName = await reverseGeocode( latitude, longitude );
            setLat( latitude );
            setLon( longitude );
            setPlace( placeName );
            if ( popupOverlay !== null )
            {
                popupOverlay.setPosition( event.coordinate );
                map.getView().setCenter( event.coordinate );
            }
            setFirstClick( false );
        };
        map.on( "singleclick", handleMapClick );
        return () =>
        {
            map.un( "singleclick", handleMapClick );
            map.setTarget( undefined ); // null
        };
    }, [ mapRef, popupRef, curr, popup ] );

    const isInvalidLocation = lat === undefined || lon === undefined || !isValidPlacename( place );
    return (
        <Modal open = {open} onClose = {handleClose}>
            <Box sx = {{
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                position: "absolute",
                width: 400,
                bgcolor: "background.paper",
                boxShadow: 24,
                p: 4
            }}
            >
                <Typography fontSize = {16}>{LOCATION_SELECT_MODAL_INSTRUCTIONS}</Typography>
                <p></p>
                <div
                    style = {{
                        width: "100%",
                        height: "300px",
                    }}
                    id = "locationChooserMap"
                    ref = {mapRef}
                    className = "map-container"
                >
                    <FontAwesomeIcon ref = {popupRef} style = {{ position: "relative", top: "15px" }} icon = {faLocationDot}/>
                </div>
                <TextField label = "Latitude" value = {lat ?? ""}
                    InputProps = {{ disableUnderline: true, readOnly: true }}
                    variant = "standard" fullWidth
                    margin = "normal"
                />
                <TextField label = "Longitude" value = {lon ?? ""}
                    InputProps = {{ disableUnderline: true, readOnly: true }}
                    variant = "standard" fullWidth
                    margin = "normal"
                />
                <TextField label = "Place" value = {place}
                    InputProps = {{ disableUnderline: true, readOnly: true }}
                    variant = "standard" fullWidth
                    margin = "normal"
                />
                {!firstClick && isInvalidLocation && <span style = {{ color: "red" }}>{VALID_LOCATION}</span>}
                <Button onClick = {handleClose} variant = "outlined" sx = {{ marginTop: 2, marginRight: 2 }}>{DELETE_CONFIRMATION_NO}</Button>
                <Button onClick = {handleSelect} disabled = {isInvalidLocation} variant = "contained" sx = {{ marginTop: 2 }}>Select</Button>
            </Box>
        </Modal>
    );
};
export default OpenLayersModal;
